import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'

export default function Footer() {
  const { t } = useTranslation()
  const [settings, setSettings] = useState(null)
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0)
  const [logos, setLogos] = useState([])

  useEffect(() => {
    loadSettings()
  }, [])

  // Logo slideshow effect
  useEffect(() => {
    if (logos.length > 0) {
      const interval = setInterval(() => {
        setCurrentLogoIndex((prevIndex) => (prevIndex + 1) % logos.length)
      }, 3000) // Change logo every 3 seconds

      return () => clearInterval(interval)
    }
  }, [logos])

  const loadSettings = async () => {
    try {
      const docRef = doc(db, 'settings', 'global')
      const docSnap = await getDoc(docRef)
      if (docSnap.exists()) {
        const data = docSnap.data()
        setSettings(data)

        // Setup logo array from settings
        const logoArray = []
        if (data.logo1Url) logoArray.push(data.logo1Url)
        if (data.logo2Url) logoArray.push(data.logo2Url)
        if (data.logo3Url) logoArray.push(data.logo3Url)

        // Fallback to default if no logos in settings
        if (logoArray.length === 0) {
          logoArray.push('/images/logo-1.png', '/images/logo-2.png', '/images/logo-3.png')
        }

        setLogos(logoArray)
      }
    } catch (error) {
      console.error('Error loading settings:', error)
      // Fallback logos
      setLogos(['/images/logo-1.png', '/images/logo-2.png', '/images/logo-3.png'])
    }
  }

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section logo-section">
            {logos.length > 0 && (
              <div className="logo-slideshow">
                <img
                  src={logos[currentLogoIndex]}
                  alt="DelyTour Logo"
                  className="footer-logo"
                />
              </div>
            )}
            <h3 className="site-name">
              {settings?.siteName || 'DelyTour'}
            </h3>
            <p>{settings?.address || 'Khám phá Việt Nam cùng chúng tôi'}</p>
            {settings?.facebook && (
              <a href={settings.facebook} target="_blank" rel="noopener noreferrer" className="social-link">
                📘 Facebook
              </a>
            )}
            {settings?.zalo && (
              <p className="social-link">💬 Zalo: {settings.zalo}</p>
            )}
          </div>

          <div className="footer-section">
            <h4>{t('footer.quickLinks')}</h4>
            <Link to="/tours">{t('header.menu.tours')}</Link>
            <Link to="/destinations">{t('header.menu.destinations')}</Link>
            <Link to="/about">{t('header.menu.about')}</Link>
            <Link to="/contact">{t('header.menu.contact')}</Link>
          </div>

          <div className="footer-section">
            <h4>{t('footer.support')}</h4>
            <Link to="/faq">{t('footer.faq')}</Link>
            <Link to="/terms">{t('footer.terms')}</Link>
            <Link to="/policy">{t('footer.policy')}</Link>
          </div>

          <div className="footer-section">
            <h4>{t('footer.contact')}</h4>
            <p>📧 {t('footer.email')}: {settings?.supportEmail || 'contact@delytour.com'}</p>
            <p>📱 {settings?.primaryPhone || '+84 123 456 789'}</p>
            {settings?.hotline && settings.hotline !== settings.primaryPhone && (
              <p>☎️ {t('footer.hotline')}: {settings.hotline}</p>
            )}
          </div>
        </div>

        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} {settings?.siteName || 'DelyTour'}. {t('footer.allRightsReserved')}.</p>
        </div>
      </div>

      <style>{`
        .footer {
          background: #1f2937;
          color: white;
          padding: 48px 0 24px;
          margin-top: 80px;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .footer-content {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 40px;
          margin-bottom: 40px;
        }

        .footer-section.logo-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .logo-slideshow {
          margin-bottom: 16px;
          width: 120px;
          height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .footer-logo {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          border-radius: 20px;
          animation: fadeIn 0.5s ease-in-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0.5;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .site-name {
          font-size: 1.5rem;
          margin-bottom: 12px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 700;
        }

        .footer-section h3 {
          font-size: 1.5rem;
          margin-bottom: 16px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .footer-section h4 {
          margin-bottom: 16px;
          font-size: 1.1rem;
        }

        .footer-section a {
          display: block;
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          margin-bottom: 8px;
          transition: color 0.2s;
        }

        .footer-section a:hover {
          color: white;
        }

        .footer-section p {
          color: rgba(255, 255, 255, 0.8);
          margin: 8px 0;
        }

        .social-link {
          color: rgba(255, 255, 255, 0.8) !important;
          margin: 8px 0;
          display: block;
        }

        .footer-bottom {
          text-align: center;
          padding-top: 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .footer-bottom p {
          color: rgba(255, 255, 255, 0.6);
          margin: 0;
        }

        @media (max-width: 768px) {
          .footer-content {
            grid-template-columns: 1fr;
            gap: 32px;
          }
        }
      `}</style>
    </footer>
  )
}

